import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import MembershipUpsellSection from '@/components/MembershipUpsellSection';
import type { UserMembership, UserProfile } from '@/src/api/authApi';
import type { Membership } from '@/src/api/membershipsApi';
import {
  heldMembership,
  lapsingIn,
  LONG_COPY_PLAN,
  PREVIEW_CATALOG,
  previewSession,
  previewUser,
} from '@/src/lib/membershipFixtures';
import { deriveMembershipUpsell } from '@/src/lib/membershipUpsell';

/**
 * ── /components/membership-upsell-section ───────────────────────────────
 *
 * Every state `deriveMembershipUpsell` produces, drawn through the real
 * component. Each state is a catalog + a user fed through the live rule
 * (src/lib/membershipUpsell.ts), not a hand-arranged array — so a block
 * breaks here when the rule changes, not just when someone forgets to
 * update a mock.
 *
 * The section draws WHITE text — it only ever appears inside home's
 * `$color.brandDark` Access Pass block — so every state below is wrapped in
 * that same surface, or the copy would read as a bug on this near-white
 * gallery page.
 * ─────────────────────────────────────────────────────────────────────────
 */

const PREVIEW_UPSELL_STATES: {
  id: string;
  label: string;
  plans: Membership[];
  user: UserProfile | null;
  pastMemberships: UserMembership[];
}[] = [
  {
    id: 'signed-out',
    label:
      'Signed out (or signed in, never bought) — START NOW, the whole catalog, cheapest first',
    plans: PREVIEW_CATALOG,
    user: null,
    pastMemberships: [],
  },
  {
    id: 'trial-spent',
    label:
      'Ran out of the Trial Pass, never on anything else — ENJOYED YOUR TRIAL?, and the spent trial is not drawn at all',
    plans: PREVIEW_CATALOG,
    ...previewSession(null, [heldMembership('trial', 'Trial Pass')]),
  },
  {
    id: 'trial-then-access-pass',
    label:
      'Took the Trial Pass, then an Access Pass that has since ended — an ex-member, not a trial graduate, so START NOW',
    plans: PREVIEW_CATALOG,
    ...previewSession(null, [
      { ...heldMembership('trial', 'Trial Pass'), phase: 'past' },
      { ...heldMembership('access_pass', 'Access Pass'), phase: 'past' },
    ]),
  },
  {
    id: 'ex-member',
    label:
      'Ex-member who never took the Trial Pass — the trial is for first-time members, so it is not offered at all (not even as "Already used")',
    plans: PREVIEW_CATALOG,
    ...previewSession(null, [
      { ...heldMembership('access_pass', 'Access Pass'), phase: 'past' },
    ]),
  },
  {
    id: 'on-access-pass',
    label:
      'On the Access Pass — UPGRADE, and only what costs MORE is offered (the ₹999 pass is not an upgrade)',
    plans: PREVIEW_CATALOG,
    ...previewSession(heldMembership('access_pass', 'Access Pass')),
  },
  {
    id: 'on-trial-now',
    label:
      'On the Trial Pass with perks left, which is ALSO in pastMemberships — UPGRADE, the dearer plans offered',
    plans: PREVIEW_CATALOG,
    ...previewSession(heldMembership('trial', 'Trial Pass'), [
      heldMembership('trial', 'Trial Pass'),
    ]),
  },
  {
    id: 'on-trial-exhausted',
    label:
      'On the Trial Pass with every included perk used up — still running, but ENJOYED YOUR TRIAL?. A zero-limit perk is one the trial never included, so it does not hold this back',
    plans: PREVIEW_CATALOG,
    ...previewSession({
      ...heldMembership('trial', 'Trial Pass'),
      perksUsage: {
        resetsAt: null,
        perks: {
          normal: {
            label: 'Events',
            bookings: 2,
            canBook: false,
            isUnlimited: false,
            limit: 2,
            remaining: 0,
          },
          secret_menu: {
            label: 'Secret Menu',
            bookings: 0,
            canBook: false,
            isUnlimited: false,
            limit: 0,
            remaining: 0,
          },
        },
      },
    }),
  },
  {
    id: 'plan-off-catalog',
    label:
      'On a deactivated plan — the catalog only carries active ones, so there is no price to compare against and everything else is offered rather than nothing',
    plans: PREVIEW_CATALOG,
    ...previewSession(heldMembership('founders', "Founders' Circle")),
  },
  {
    id: 'cancelled',
    label:
      'CANCELLED membership — KEEP YOUR MEMBERSHIP, the date the benefits stop, then the plan they are losing (Renew) above the upgrades',
    plans: PREVIEW_CATALOG,
    ...previewSession(
      heldMembership('access_pass', 'Access Pass', lapsingIn(24))
    ),
  },
  {
    id: 'cancelled-one-shot',
    label:
      'Cancelled ONE-SHOT pass with perks left — nothing to renew, so no Renew card; still running, so UPGRADE with its end date, and the subscriptions above it are the offer',
    plans: PREVIEW_CATALOG,
    ...previewSession(heldMembership('trial', 'Trial Pass', lapsingIn(9))),
  },
  {
    id: 'confirming',
    label:
      'A purchase still pending at the gateway — NOTHING renders below this caption. The screen shows the pending-payment banner instead',
    plans: PREVIEW_CATALOG,
    user: {
      ...previewUser(null),
      pendingMembership: {
        ...heldMembership('access_pass', 'Access Pass'),
        status: 'pending',
        phase: 'upcoming',
        perksUsage: null,
      },
    },
    pastMemberships: [],
  },
  {
    id: 'top-of-catalog',
    label:
      'On the dearest plan — NOTHING renders below this caption. The section returns null rather than an empty heading',
    plans: PREVIEW_CATALOG,
    ...previewSession(heldMembership('premium', 'Access Pass Premium')),
  },
  {
    id: 'long-copy',
    label:
      'Long name + long description + an unseeded tier — 2-line name clamp, 3-line body clamp, fallback icon',
    plans: [LONG_COPY_PLAN],
    user: null,
    pastMemberships: [],
  },
];

export default function MembershipUpsellSectionPreview() {
  return (
    <>
      <Head>
        <title>MembershipUpsellSection · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" py="$lg">
          {PREVIEW_UPSELL_STATES.map((state) => {
            const upsell = deriveMembershipUpsell(state);
            return (
              <YStack
                key={state.id}
                alignSelf="stretch"
                bg="$color.brandDark"
                px="$space.lg"
                py="$space.xl"
                gap="$lg"
              >
                <Text
                  fontFamily="$body"
                  fontSize="$small"
                  color="$textWhiteMuted"
                >
                  {state.label}
                </Text>
                <MembershipUpsellSection
                  upsell={upsell}
                  onSelectMembership={() => {}}
                />
              </YStack>
            );
          })}
        </YStack>
      </ScrollView>
    </>
  );
}
